import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useHistory, useParams } from 'react-router-dom'
import { useLazyQuery, useMutation, useSubscription } from '@apollo/client'
import { Form, Input, Select, notification } from 'antd'
import CardHeader from 'Components/CommonHeader/CardHeader'
import CardSubMenu from 'Menus/CardSubMenu'
import CommonDiv from 'Components/CommonDiv'
import CommonButton, { ButtonLoader } from 'Components/CommonButton'
import { FormDivision, FormSection } from 'Components/Form'
import { GET_LOCATION_GROUPS } from 'Util/queries'
import { COLLECTION_LOCATIONS } from 'Util/subscriptions'
import { ADD_LOCATION_GROUP, UPDATE_LOCATION_GROUP } from 'Util/mutations'

const Container = styled.div``

// PM-91: don't filter the Collection Locations picker by auction_enabled —
// groups are organizational and should remain stable across operational
// toggles. Disabled locations are shown with an "(auction disabled)" suffix
// so admins see the state at a glance but can still include them.
function LocationGroupCard() {
    const history = useHistory()
    const params = useParams()
    const [form] = Form.useForm()
    const editMode = !!params.id
    const [cardName, setCardName] = useState(editMode ? '' : 'New Group')
    const [saving, setSaving] = useState(false)

    const { data: locData, loading: locLoading } = useSubscription(COLLECTION_LOCATIONS, {
        variables: { where: { deleted_at: { _is_null: true } } },
    })
    const locations = locData?.collection_locations ?? []

    const [getGroup] = useLazyQuery(GET_LOCATION_GROUPS, {
        fetchPolicy: 'network-only',
        onCompleted(data) {
            const g = data?.location_groups?.[0]
            if (!g) return
            form.setFieldsValue({
                label: g.label,
                description: g.description,
                location_ids: g.location_ids ?? [],
            })
            setCardName(g.label)
        },
    })

    const [addGroup] = useMutation(ADD_LOCATION_GROUP)
    const [updateGroup] = useMutation(UPDATE_LOCATION_GROUP)

    useEffect(() => {
        if (editMode) {
            getGroup({ variables: { where: { id: { _eq: parseInt(params.id) } } } })
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const submitHandler = async (values) => {
        setSaving(true)
        const payload = {
            label: values.label?.trim(),
            description: values.description?.trim() || null,
            location_ids: values.location_ids ?? [],
        }
        try {
            if (editMode) {
                await updateGroup({ variables: { id: parseInt(params.id), _set: payload } })
                notification.success({ message: 'Group updated' })
            } else {
                await addGroup({ variables: { object: payload } })
                notification.success({ message: 'Group created' })
            }
            setTimeout(() => history.goBack(), 800)
        } catch (err) {
            notification.error({ message: 'Save failed', description: err.message })
            setSaving(false)
        }
    }

    return (
        <Container>
            <CardHeader title='Location Group' itemName={cardName} />
            <CardSubMenu items={[<label>Process</label>]} />

            <Form form={form} name='locationGroupCard' onFinish={submitHandler}>
                <FormSection title='General'>
                    <FormDivision>
                        <Form.Item
                            label='Group Label'
                            name='label'
                            rules={[{ required: true, message: 'Label is required' }]}
                        >
                            <Input placeholder='e.g. Northern Fishing Harbours' />
                        </Form.Item>

                        <Form.Item label='Description' name='description'>
                            <Input.TextArea rows={3} placeholder='Optional' />
                        </Form.Item>

                        <Form.Item
                            label='Collection Locations'
                            name='location_ids'
                            rules={[{ required: true, message: 'Pick at least one location' }]}
                            initialValue={[]}
                        >
                            <Select
                                mode='multiple'
                                loading={locLoading}
                                placeholder='Select collection locations'
                                optionFilterProp='label'
                                options={locations.map(l => ({
                                    value: l.id,
                                    label: l.auction_enabled
                                        ? `${l.location_code} — ${l.location_name}`
                                        : `${l.location_code} — ${l.location_name} (auction disabled)`,
                                }))}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </FormDivision>
                </FormSection>

                <CommonDiv direction='row' justify='flex-end'>
                    <ButtonLoader loading={saving} htmlType='submit'>
                        {editMode ? 'Update' : 'Save'}
                    </ButtonLoader>
                    <CommonButton className='reverse' onClick={() => history.goBack()}>
                        Cancel
                    </CommonButton>
                </CommonDiv>
            </Form>
        </Container>
    )
}

export default LocationGroupCard
